import { Chevron } from "@components/icons"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body16MainStyles, paragraphStyles } from "@constants/typography"
import { getProductURL } from "@utils/urls"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import DeviceDropdown from "./DeviceDropdown"
import SeriesBlock from "./SeriesBlock"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: auto;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: column-reverse;
  }
`

const Variant = styled.div`
  flex-direction: column;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  display: flex;
  width: 100%;
  overflow: visible;
  z-index: 2;

  @media ${breakpoints.laptop} {
    justify-content: flex-start;
  }
`

const SelectText = styled.div`
  ${paragraphStyles}
  margin-bottom: 8px;
  color: ${colors.mediumDarkGray};
`

const Devices = styled.div`
  position: relative;
  width: 100%;
`

const DeviceButton = styled.a<{ $canClick?: boolean }>`
  display: flex;
  width: 100%;
  height: 56px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: auto;
  min-height: 56px;
  margin-top: 4px;
  padding: 0 12px;
  border: 1px solid ${colors.lightGray};
  cursor: pointer;
  ${({ $canClick }) => (!$canClick ? "cursor: initial" : "")}
`

const DeviceButtonText = styled.div`
  ${body16MainStyles}
  display: flex;
  flex-wrap: wrap;
  opacity: 0.9;
  font-weight: 400;
  color: ${colors.black};
`

const ChevronIcon = styled.div`
  height: 32px;
  width: 32px;
  transform: rotate(90deg);

  @media ${breakpoints.tablet} {
    margin-right: 6px;
  }
`

const Series = styled.div`
  width: 100%;
  margin-top: 24px;
`

const SeriesList = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  overflow-x: auto;
`

interface ProductFormProps {
  currentVariant: VariantProps
  device?: string
  onVariantChange(variant: number)
  relatedSeries: any[]
  series?: string
  slug: string
  variants: VariantProps[]
}

interface VariantProps {
  color?: string
  deviceBrandName?: string
  deviceBrandSlug?: string
  deviceName?: string
  deviceSlug?: string
  hexString?: string
  images: Array<string>
  isUnavailable?: boolean
  language?: string
  name: string
  price: string
  size?: string
  sku: string
  swatchImage?: string
}

const ProductForm: React.FC<ProductFormProps> = (props) => {
  const [deviceDropdownIsOpen, setDeviceDropdownIsOpen] = React.useState(false)

  const getClickHandlerForVariantDevice = (variantDevice) => (e) => {
    e.stopPropagation()
    const variantIndex =
      variantDevice &&
      R.findIndex(({ deviceSlug }) => {
        return variantDevice === deviceSlug
      })(props.variants as any)

    props.onVariantChange(variantIndex)
    setDeviceDropdownIsOpen(false)
  }

  const variantDevices: any =
    R.pipe(
      R.reject(({ deviceName }: any) => !deviceName) as any,
      R.uniqBy(R.prop("deviceSlug")) as any,
      R.map(({ deviceBrandName, deviceName, deviceSlug }: any) => ({
        brand: deviceBrandName,
        device: deviceName,
        onClick: getClickHandlerForVariantDevice(deviceSlug),
      }))
    )(props.variants as any) || []

  const formattedRelatedSeries = React.useMemo(() => {
    if (
      !props.series ||
      !props.relatedSeries ||
      props.relatedSeries.length === 0
    ) {
      return []
    }

    const sortedRelatedSeries = props.relatedSeries
      .map((series) => {
        const variantForDevice = series.variants.find((variant) => {
          return props.device === variant.device?.name
        })
        const variantData = variantForDevice || series.variants[0]
        const variantSku = variantData.sku
        const isDisabled = !variantForDevice

        return {
          disabled: isDisabled,
          imageUrl: variantData.images[0]?.file?.url || "",
          name: series.series?.name || "",
          price: variantData.price,
          slug: getProductURL(series.slug, variantSku, props.device),
        }
      })
      .sort((a, b) => {
        if (props.series === a.name) return -1
        if (props.series === b.name) return 1
        return 0
      })

    if (sortedRelatedSeries.length > 1) {
      const activeSeries = sortedRelatedSeries.findIndex(
        (series) => series.name === props.series
      )

      if (activeSeries !== -1 && activeSeries !== 0) {
        return [
          sortedRelatedSeries[activeSeries],
          ...sortedRelatedSeries.slice(0, activeSeries),
          ...sortedRelatedSeries.slice(activeSeries + 1),
        ]
      }
    }

    return sortedRelatedSeries
  }, [props.device, props.series])

  return (
    <Container>
      <Variant>
        {variantDevices.length > 0 && (
          <Devices>
            <DeviceDropdown
              currentDevice={props.currentVariant?.deviceName || ""}
              devices={variantDevices}
              isOpen={deviceDropdownIsOpen}
              onRequestClose={() => setDeviceDropdownIsOpen(false)}
            />
            <SelectText>Select device:</SelectText>
            <DeviceButton
              $canClick={true}
              as="a"
              onClick={(e) => {
                e.stopPropagation()
                setDeviceDropdownIsOpen(true)
              }}
              role="button"
              tabIndex={0}
            >
              <DeviceButtonText>
                {props.currentVariant?.deviceName}
              </DeviceButtonText>
              <ChevronIcon>
                <Chevron strokeColor={colors.black} />
              </ChevronIcon>
            </DeviceButton>
          </Devices>
        )}
        {!props.slug.includes("intl") && formattedRelatedSeries.length > 0 && (
          <Series>
            <SelectText>Select series:</SelectText>
            <SeriesList>
              {formattedRelatedSeries.map((series, i) => (
                <SeriesBlock
                  active={props.series === series.name}
                  compact={formattedRelatedSeries.length > 3}
                  disabled={series.disabled}
                  imageUrl={series.imageUrl}
                  key={i}
                  name={series.name}
                  price={series.price}
                  slug={series.slug}
                />
              ))}
            </SeriesList>
          </Series>
        )}
      </Variant>
    </Container>
  )
}

export default ProductForm
